import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';

export interface TabSettings {
  showPatientTimeline: boolean;
  showInsightsAndActions: boolean;
  showPredictions: boolean;
  showSharedWellbeingPlan: boolean;
  showInsightSettings: boolean;
  showInvitePatient: boolean;
  showInterventionSettings: boolean;
  showOsaNavigatorApp: boolean;
}

interface TabSettingsReturn extends TabSettings {
  shouldDisplayLeftNavbar: boolean;
}

const defaultTabSettings: TabSettings = {
  showPatientTimeline: true,
  showInsightsAndActions: true,
  showPredictions: true,
  showSharedWellbeingPlan: true,
  showInsightSettings: true,
  showInvitePatient: false,
  showInterventionSettings: false,
  showOsaNavigatorApp: false,
};

export const useTabSettings = (): TabSettingsReturn => {
  const [tabSettings, setTabSettings] =
    useState<TabSettings>(defaultTabSettings);
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  useEffect(() => {
    if (!selectedTenancy) {
      // reset to default when no tenancy is selected
      setTabSettings(defaultTabSettings);
      return;
    }
    if (selectedTenancy?.features?.patientProfileTabSettings) {
      // update tab settings based on selected tenancy features
      setTabSettings((currentSettings) => ({
        ...currentSettings,
        ...selectedTenancy.features?.patientProfileTabSettings,
      }));
    }
  }, [selectedTenancy]);

  const shouldDisplayLeftNavbar = useMemo(() => {
    // calculate if tabSettings contain any true
    const displayedTabs = Object.values(tabSettings).filter((value) => value);
    const hasDisplayedTab = displayedTabs.length > 1;
    return hasDisplayedTab;
  }, [tabSettings]);

  return {
    ...tabSettings,
    shouldDisplayLeftNavbar,
  };
};
